// The Schedule of Work's document settings: which hours, which columns,
// photos on or off (docs/SCHEDULE-OF-WORK.md "The options screen").
import type { HoursSource, ScheduleOptions } from '../lib/schedule/model';

const SOURCES: { key: HoursSource; label: string; hint: string }[] = [
  { key: 'none', label: 'Scope only', hint: 'No hours, litres or quantities' },
  { key: 'measured', label: 'Measured', hint: 'From the measurements and production rates' },
  { key: 'approved', label: 'Approved', hint: 'As priced: the hours the REVIEW chose for each area' },
];

const pill = (on: boolean) => `min-h-10 rounded-full border px-3 text-sm ${on ? 'border-primary bg-orange-50 text-orange-900' : 'border-slate-300 text-slate-600'}`;

interface Props {
  options: ScheduleOptions;
  /** Sources no area has figures for are offered but marked. */
  available: Record<HoursSource, boolean>;
  onChange: (change: Partial<ScheduleOptions>) => void;
}

export function ScheduleSettings({ options, available, onChange }: Props) {
  const figures = options.hoursSource !== 'none';
  return (
    <section className="space-y-3 rounded-lg border border-slate-200 bg-white p-4">
      <h2 className="font-medium text-slate-900">Hours and figures</h2>
      <div className="grid gap-2 sm:grid-cols-2">
        {SOURCES.map((s) => (
          <button
            key={s.key}
            type="button"
            aria-pressed={options.hoursSource === s.key}
            onClick={() => onChange({ hoursSource: s.key })}
            className={`rounded-lg border p-3 text-left ${options.hoursSource === s.key ? 'border-primary bg-orange-50' : 'border-slate-200'}`}
          >
            <span className="block text-sm font-medium text-slate-900">{s.label}</span>
            <span className="block text-xs text-slate-500">{available[s.key] ? s.hint : `${s.hint} — no area has any; measured hours are used`}</span>
          </button>
        ))}
      </div>
      {figures && (
        <div className="flex flex-wrap items-center gap-2 text-sm">
          <span className="text-slate-600">Show:</span>
          <button type="button" aria-pressed={options.showM2} onClick={() => onChange({ showM2: !options.showM2 })} className={pill(options.showM2)}>
            Quantities
          </button>
          <button type="button" aria-pressed={options.showHours} onClick={() => onChange({ showHours: !options.showHours })} className={pill(options.showHours)}>
            Hours
          </button>
          <button type="button" aria-pressed={options.showLitres} onClick={() => onChange({ showLitres: !options.showLitres })} className={pill(options.showLitres)}>
            Litres
          </button>
          <button type="button" aria-pressed={options.showSurfaces} onClick={() => onChange({ showSurfaces: !options.showSurfaces })} className={pill(options.showSurfaces)}>
            Surface breakdown
          </button>
        </div>
      )}
      <label className="flex min-h-10 items-center gap-2 text-sm">
        <input type="checkbox" checked={options.includePhotos} onChange={(e) => onChange({ includePhotos: e.target.checked })} className="h-5 w-5" />
        Include photos
      </label>
    </section>
  );
}
